import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, MoreVertical, Pencil, Trash2, User, Calendar, Building, CheckSquare, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetDealQuery,
  useGetDealStagesQuery,
  useDeleteDealMutation,
  useGetDealActivitiesQuery,
  useGetTasksQuery,
} from "@/store/rest_api";
import { Checkbox } from "@/components/ui/checkbox";
import { useUpdateTaskStatusMutation } from "@/store/rest_api";
import TaskFormDialog from "@/components/pages/tasks/TaskFormDialog";
import { toast } from "sonner";
import DealStage from "@/model/interface/DealStage";
import { DealFormDialog } from "@/components/pages/deals/DealFormDialog";
import { DealActivityFeed } from "@/components/pages/deals/DealActivityFeed";
import { getContactDisplayName } from "@/util/string";
import ContactStatePill from "@/components/contacts/ContactStatePill";
import { PropertyDetailCard } from "@/components/pages/properties/PropertyDetailCard";
import DealStagePicker from "@/components/deals/DealStagePicker";
import { useMoveDealStage } from "@/hooks/useMoveDealStage";
import { memberName } from "@/util/member";
import { ContactType } from "@/enum/ContactType";

export default function DealDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);

  const { data: deal, isLoading } = useGetDealQuery(id!);
  const { data: stages = [] as DealStage[] } = useGetDealStagesQuery({});
  const { data: activitiesData } = useGetDealActivitiesQuery({ dealId: id! });
  const { data: dealTasks } = useGetTasksQuery({ dealId: id!, size: 20 });
  const { moveDealStage, isMoving } = useMoveDealStage();
  const [deleteDeal] = useDeleteDealMutation();
  const [updateTaskStatus] = useUpdateTaskStatusMutation();
  const [showTaskDialog, setShowTaskDialog] = useState(false);

  const formatCurrency = (value?: number) => {
    if (value == null) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this deal?")) return;
    try {
      await deleteDeal(id!).unwrap();
      toast.success("Deal deleted");
      navigate("/deals");
    } catch {
      toast.error("Failed to delete deal");
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Deal not found</p>
      </div>
    );
  }

  const contactName = getContactDisplayName(deal.contact);

  const activities = activitiesData?.items || [];

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/deals")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">{deal.propertyAddress || contactName || "Deal"}</h1>
          <Badge variant="outline" style={{ borderColor: deal.stage?.color, color: deal.stage?.color }}>
            {deal.stage?.name}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowEditDialog(true)}>
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" title="Options">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-destructive" onClick={handleDelete}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete deal
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          {deal.property ? (
            <PropertyDetailCard property={deal.property} />
          ) : deal.propertyAddress || deal.propertyCity ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Property</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {deal.propertyAddress && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{deal.propertyAddress}</span>
                  </div>
                )}
                {deal.propertyCity && (
                  <p className="text-sm text-muted-foreground ml-6">
                    {deal.propertyCity}
                    {deal.propertyState ? `, ${deal.propertyState}` : ""} {deal.propertyZip || ""}
                  </p>
                )}
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Financial Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Offer Price</p>
                  <p className="text-lg font-semibold">{formatCurrency(deal.offerPrice)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">ARV</p>
                  <p className="text-lg font-semibold">{formatCurrency(deal.arv)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Repair Estimate</p>
                  <p className="text-lg font-semibold">{formatCurrency(deal.repairEstimate)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Assignment Fee</p>
                  <p className="text-lg font-semibold">{formatCurrency(deal.assignmentFee)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Profit</p>
                  <p className="text-lg font-semibold text-green-600">{formatCurrency(deal.profit)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Closing Date</p>
                  <p className="text-lg font-semibold">
                    {deal.closingDate ? new Date(deal.closingDate).toLocaleDateString() : "—"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {deal.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{deal.notes}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <DealActivityFeed activities={activities} />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Stage</CardTitle>
            </CardHeader>
            <CardContent>
              <DealStagePicker
                stages={stages}
                currentStageId={deal.stage?.id}
                personIsLead={deal.contact?.type === ContactType.LEAD}
                disabled={isMoving}
                onPick={(stage) => void moveDealStage(deal, stage.id)}
              />
            </CardContent>
          </Card>

          {contactName && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span
                    className="text-sm cursor-pointer hover:underline"
                    onClick={() => navigate(`/contacts/${deal.contact!.id}/detail`)}
                  >
                    {contactName}
                  </span>
                  <ContactStatePill contact={deal.contact} />
                </div>
                {deal.contact?.company && (
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{deal.contact.company}</span>
                  </div>
                )}
                {deal.contact?.phones && deal.contact.phones.length > 0 && (
                  <p className="text-sm text-muted-foreground ml-6">{deal.contact.phones[0].phoneNumber}</p>
                )}
              </CardContent>
            </Card>
          )}

          {deal.assignedMembership && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Assigned To</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {memberName(deal.assignedMembership)}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {deal.source && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Source</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{deal.source}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckSquare className="h-4 w-4" />
                  Tasks
                </div>
                <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setShowTaskDialog(true)}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dealTasks && dealTasks.items.length > 0 ? (
                <div className="space-y-2">
                  {dealTasks.items.map((task) => {
                    const overdue =
                      task.dueDate &&
                      task.status !== "DONE" &&
                      new Date(task.dueDate) < new Date(new Date().toDateString());
                    return (
                      <div key={task.id} className="flex items-start gap-2">
                        <Checkbox
                          className="mt-0.5"
                          checked={task.status === "DONE"}
                          onCheckedChange={() => {
                            const newStatus = task.status === "DONE" ? "TODO" : "DONE";
                            updateTaskStatus({ id: task.id, status: newStatus });
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <span
                            className={`text-sm ${task.status === "DONE" ? "line-through text-muted-foreground" : ""}`}
                          >
                            {task.title}
                          </span>
                          {task.dueDate && (
                            <p
                              className={`text-xs flex items-center gap-1 ${overdue ? "text-red-500 font-medium" : "text-muted-foreground"}`}
                            >
                              <Calendar className="h-3 w-3" />
                              {new Date(task.dueDate).toLocaleDateString()}
                              {overdue && " (overdue)"}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No tasks</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Dates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                Created: {deal.createdAt ? new Date(deal.createdAt).toLocaleDateString() : "—"}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                Updated: {deal.updatedAt ? new Date(deal.updatedAt).toLocaleDateString() : "—"}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <DealFormDialog open={showEditDialog} onOpenChange={setShowEditDialog} deal={deal} />

      <TaskFormDialog
        open={showTaskDialog}
        onOpenChange={setShowTaskDialog}
        defaultDealId={id}
        defaultContactId={deal.contact?.id}
      />
    </div>
  );
}
